/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  DeviceAlertsProperties,
  DeviceAlertsRequest,
  DeviceAlertsResponse,
  DeviceAlertsInitialProperties,
} from '../../interface/device-alerts';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import device from '../../utils/api/device';

// a thunk to fetch users
export const fetchDeviceAlerts = createAsyncThunk<DeviceAlertsResponse, DeviceAlertsRequest>(
  'device-alerts/fetchDeviceAlerts',
  async (data: DeviceAlertsRequest, { dispatch }) => {
    try {
      const response = await device.fetchDeviceAlerts(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchListAlertsFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('device-alerts/fetchListAlertsFilters', async (_, { dispatch }) => {
  try {
    const response = await device.fetchListTransactionFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const deviceFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: DeviceAlertsInitialProperties = {
  alerts: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: deviceFilter,
  filterListLoading: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const DeviceAlertsSlice = createSlice({
  name: 'device-alerts',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param state
     * @param action
     */
    updateAlertsFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter } = UpdateCommonFilter(state.filters, action.payload);
        state.filters = updatedCommonFilter;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
      }
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeviceAlerts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDeviceAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload?.results?.length
          ? action.payload.results?.map((eachSchedule: DeviceAlertsProperties) => ({
              ...eachSchedule,
              id: eachSchedule._id,
              date: ConvertUTCtoDate(eachSchedule?.createdAt || ''),
              time: FormatUtcString(ConvertUTCtoHHMM(eachSchedule?.createdAt || '')),
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchDeviceAlerts.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchListAlertsFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListAlertsFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListAlertsFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateAlertsFilters, setSortData } = DeviceAlertsSlice.actions;
// Export the reducer
export default DeviceAlertsSlice.reducer;
